import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Alert,
  AlertDescription,
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@brent/core-ui/ui'
import { Plus } from 'lucide-react'
import { useQuestions, useCreateQuestion } from '@/hooks/useQuestions'
import { useToast } from '@/hooks/use-toast'
import QuestionForm from '@/components/features/questions/QuestionForm'
import type { CreateQuestionInput } from '@/services/api/questions'

const statusVariants: Record<string, 'default' | 'secondary' | 'destructive' | 'outline'> = {
  draft: 'secondary',
  live: 'default',
  archived: 'outline',
}

export function Questions() {
  const navigate = useNavigate()
  const { data: questions, isLoading, error } = useQuestions()
  const createMutation = useCreateQuestion()
  const { toast } = useToast()
  const [createOpen, setCreateOpen] = useState(false)

  const handleCreate = async (input: CreateQuestionInput) => {
    try {
      const question = await createMutation.mutateAsync(input)
      toast({ title: 'Question created', description: 'The question has been saved as a draft.' })
      setCreateOpen(false)
      navigate(`/questions/${question.question_id}`)
    } catch (err) {
      toast({
        variant: 'destructive',
        title: 'Failed to create question',
        description: err instanceof Error ? err.message : 'An error occurred',
      })
    }
  }

  if (isLoading) {
    return (
      <div className="space-y-6">
        <h1 className="text-2xl font-bold">Questions</h1>
        <div className="space-y-4">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="h-16 animate-pulse rounded-lg bg-muted" />
          ))}
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="space-y-6">
        <h1 className="text-2xl font-bold">Questions</h1>
        <Alert variant="destructive">
          <AlertDescription>
            Failed to load questions. {error instanceof Error ? error.message : 'Unknown error'}
          </AlertDescription>
        </Alert>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">Questions</h1>
        <Dialog open={createOpen} onOpenChange={setCreateOpen}>
          <DialogTrigger asChild>
            <Button>
              <Plus className="mr-2 h-4 w-4" />
              New Question
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Create New Question</DialogTitle>
              <DialogDescription>
                Create a question for members to vote on. It will start as a draft.
              </DialogDescription>
            </DialogHeader>
            <QuestionForm onSubmit={handleCreate} isPending={createMutation.isPending} />
          </DialogContent>
        </Dialog>
      </div>

      {!questions || questions.length === 0 ? (
        <Card>
          <CardHeader>
            <CardTitle>No questions yet</CardTitle>
            <CardDescription>
              Create your first question to get started with member voting.
            </CardDescription>
          </CardHeader>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Statement</TableHead>
                  <TableHead className="w-24">Status</TableHead>
                  <TableHead className="w-24">Options</TableHead>
                  <TableHead className="w-36">Created</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {questions.map((q) => (
                  <TableRow
                    key={q.question_id}
                    className="cursor-pointer"
                    onClick={() => navigate(`/questions/${q.question_id}`)}
                  >
                    <TableCell className="font-medium">
                      {q.statement.length > 80 ? q.statement.slice(0, 80) + '...' : q.statement}
                    </TableCell>
                    <TableCell>
                      <Badge variant={statusVariants[q.status] ?? 'outline'}>{q.status}</Badge>
                    </TableCell>
                    <TableCell>{q.options.length}</TableCell>
                    <TableCell className="text-muted-foreground">
                      {new Date(q.created_at).toLocaleDateString()}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  )
}
